import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import { DeleteOutlineOutlined, Add } from "@mui/icons-material";
import CustomDialog from "../../components-new/common/CustomDialog";
import useDeleteResolution from "../../useQuery/useDeleteResolution";
import { useNotification } from "../../hooks";

export default function ResolutionComponent({
  product,
  setproduct,
  productList,
  resolution,
  setResolution,
  getresolution,
  onSubmitres,
}) {
  const [openModal, setOpenModal] = useState(false);
  const notify = useNotification();
  const [newResolution, setNewResolution] = useState({
    webWidth: "",
    webHeight: "",
    mobileWidth: "",
    mobileHeight: "",
  });

  const { mutate: deleteResolution, isLoading: isDeleting } = useDeleteResolution({
    onSuccess: () => {
      if (product) {
        getresolution(product);
      }
      notify("Resolution deleted successfully", "success");
    },
    onError: (error) => {
      console.error("Error deleting resolution:", error);
      notify("Failed to delete resolution. Please try again.", "error");
    },
  });

  const handleProductChange = (event) => {
    const productId = event.target.value;
    setproduct(productId);
    if (productId) {
      getresolution(productId);
    } else {
      setResolution({ web: [], mobile: [] });
    }
  };

  const handleOpenModal = () => {
    setNewResolution({
      webWidth: "",
      webHeight: "",
      mobileWidth: "",
      mobileHeight: "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewResolution({
      webWidth: "",
      webHeight: "",
      mobileWidth: "",
      mobileHeight: "",
    });
  };

  const handleSaveNewResolution = () => {
    onSubmitres(newResolution);
    handleCloseModal();
  };

  const isFormValid = () => {
    return (
      (newResolution.webWidth && newResolution.webHeight) ||
      (newResolution.mobileWidth && newResolution.mobileHeight)
    );
  };

  const removeResolution = (type, index) => {
    const resolutionToDelete = resolution[type][index];
    if (!resolutionToDelete || !resolutionToDelete.id) {
      console.error("Resolution ID not found");
      return;
    }

    deleteResolution({
      productId: product,
      resolutionId: resolutionToDelete.id,
    });
  };

  const modalContent = (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Web Resolution
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            type="number"
            value={newResolution.webWidth}
            onChange={(e) => setNewResolution((prev) => ({ ...prev, webWidth: e.target.value }))}
            placeholder="Width"
            size="small"
            label="Width"
            fullWidth
          />
          <Typography sx={{ color: "#999" }}>×</Typography>
          <TextField
            type="number"
            value={newResolution.webHeight}
            onChange={(e) => setNewResolution((prev) => ({ ...prev, webHeight: e.target.value }))}
            placeholder="Height"
            size="small"
            label="Height"
            fullWidth
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Mobile Resolution
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            type="number"
            value={newResolution.mobileWidth}
            onChange={(e) => setNewResolution((prev) => ({ ...prev, mobileWidth: e.target.value }))}
            placeholder="Width"
            size="small"
            label="Width"
            fullWidth
          />
          <Typography sx={{ color: "#999" }}>×</Typography>
          <TextField
            type="number"
            value={newResolution.mobileHeight}
            onChange={(e) =>
              setNewResolution((prev) => ({ ...prev, mobileHeight: e.target.value }))
            }
            placeholder="Height"
            size="small"
            label="Height"
            fullWidth
          />
        </Box>
      </Box>
    </Box>
  );

  const modalActions = (
    <>
      <Button onClick={handleCloseModal} color="inherit">
        Cancel
      </Button>
      <Button onClick={handleSaveNewResolution} variant="contained" disabled={!isFormValid()}>
        Save
      </Button>
    </>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Resolution Settings
        </Typography>
        {product && (
          <Button variant="outlined" startIcon={<Add />} onClick={handleOpenModal}>
            Add Resolution
          </Button>
        )}
      </Box>

      <Card sx={{ p: 3 }}>
        <Box sx={{ mb: 3, maxWidth: "300px" }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Product
          </Typography>
          <Select
            value={product}
            onChange={handleProductChange}
            fullWidth
            displayEmpty
            size="small"
          >
            <MenuItem value="">
              <em>Select a product</em>
            </MenuItem>
            {productList.map((item) => (
              <MenuItem key={item.idproducts} value={item.idproducts}>
                {item.productname}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {product && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                maxHeight: "500px",
                overflowY: "auto",
                pr: 1,
                scrollbarWidth: "thin",
                scrollbarColor: "#bdbdbd #f5f5f5",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f5f5f5",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#bdbdbd",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#9e9e9e",
                  },
                },
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Web Resolutions
                </Typography>
                <Stack spacing={1}>
                  {resolution.web?.length > 0 ? (
                    resolution.web.map((item, index) => (
                      <Box
                        key={item.id || index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 0.75,
                          border: "1px solid #e0e0e0",
                          borderRadius: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: "0.875rem" }}>
                          {item.width} × {item.height}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeResolution("web", index)}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      No web resolutions added yet
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Mobile Resolutions
                </Typography>
                <Stack spacing={1}>
                  {resolution.mobile?.length > 0 ? (
                    resolution.mobile.map((item, index) => (
                      <Box
                        key={item.id || index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 0.75,
                          border: "1px solid #e0e0e0",
                          borderRadius: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: "0.875rem" }}>
                          {item.width} × {item.height}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeResolution("mobile", index)}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteOutlineOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      No mobile resolutions added yet
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>
          </>
        )}
      </Card>

      <CustomDialog
        open={openModal}
        onClose={handleCloseModal}
        title="Add New Resolution"
        content={modalContent}
        actions={modalActions}
        width="450px"
      />
    </Box>
  );
}
